import React, { useRef } from 'react';

import { useIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { HelpPageAri } from '@atlassian/ari/help';

import messages from '../messages';

import type { HelpCenterFolder, HelpCenterPage, HelpCenterSiteTreeNode } from './types';
import { useSiteNavigationOverflow } from './use-site-navigation-overflow';

const EMPTY_SITE_NAVIGATION_ITEMS: HelpCenterSiteTreeNode[] = [];

const styles = cssMap({
	// Own the flex-growing slot here so its ref is available when this
	// component's layout effect measures the remaining navbar width.
	centerSlot: {
		flexGrow: 1,
		minWidth: 0,
		position: 'relative',
	},
	measurementRow: {
		position: 'absolute',
		top: 0,
		left: 0,
		display: 'flex',
		gap: token('space.050'),
		width: 'max-content',
		visibility: 'hidden',
		pointerEvents: 'none',
	},
});

const getFolderPages = (folder: HelpCenterFolder): HelpCenterPage[] =>
	folder.contents.edges.map((edge) => edge.node);

type PageItemProps = {
	page: HelpCenterPage;
};

const PageItem = ({ page }: PageItemProps) => {
	const { push } = useRouterActions();
	const pageId = HelpPageAri.parse(page.id).pageId;

	return <DropdownItem onClick={() => push(`/page/${pageId}`)}>{page.name.default}</DropdownItem>;
};

const FolderMenuItems = ({ pages }: { pages: HelpCenterPage[] }) => (
	<DropdownItemGroup>
		{pages.map((page) => (
			<PageItem key={page.id} page={page} />
		))}
	</DropdownItemGroup>
);

type FolderMenuProps = {
	folder: HelpCenterFolder;
};

const PromotedFolderMenu = ({ folder }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="bottom-start"
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					appearance="subtle"
					iconAfter={(iconProps) => (
						<ChevronDownIcon {...iconProps} label={formatMessage(messages.downIcon)} />
					)}
					ref={triggerRef}
				>
					{folder.name.default}
				</Button>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const OverflowFolderMenu = ({ folder }: FolderMenuProps) => {
	const { formatMessage } = useIntl();

	return (
		<DropdownMenu
			placement="right-start"
			shouldFlip
			shouldRenderToParent
			trigger={({ triggerRef, ...props }) => (
				<DropdownItem
					{...props}
					elemAfter={
						<ChevronRightIcon label={formatMessage(messages.openFolderSubmenu)} size="small" />
					}
					ref={triggerRef}
				>
					{folder.name.default}
				</DropdownItem>
			)}
		>
			<FolderMenuItems pages={getFolderPages(folder)} />
		</DropdownMenu>
	);
};

const OverflowItem = ({ item }: { item: HelpCenterSiteTreeNode }) =>
	item.__typename === 'HelpCenterPage' ? (
		<PageItem page={item} />
	) : (
		<OverflowFolderMenu folder={item} />
	);

export const SiteNavigationMenus = ({
	overflowItems,
	promotedFolders,
	// Kept for API parity with MoreDropdown. The current Button entry point omits `style`, so
	// dynamic navbar textColor theming is deferred until a supported approach is available.
	textColor: _textColor,
}: {
	overflowItems: HelpCenterSiteTreeNode[];
	promotedFolders: HelpCenterFolder[];
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (promotedFolders.length === 0 && overflowItems.length === 0) {
		return null;
	}

	return (
		<>
			{promotedFolders.map((folder) => (
				<PromotedFolderMenu key={folder.id} folder={folder} />
			))}
			{overflowItems.length > 0 && (
				<DropdownMenu
					shouldRenderToParent
					trigger={({ triggerRef, ...props }) => (
						<Button
							{...props}
							appearance="subtle"
							testId="site-navigation-more"
							iconAfter={(iconProps) => (
								<ChevronDownIcon {...iconProps} label={formatMessage(messages.downIcon)} />
							)}
							ref={triggerRef}
						>
							{formatMessage(messages.more)}
						</Button>
					)}
				>
					<DropdownItemGroup>
						{overflowItems.map((item) => (
							<OverflowItem key={item.id} item={item} />
						))}
					</DropdownItemGroup>
				</DropdownMenu>
			)}
		</>
	);
};

export const SiteNavigation = ({
	queryRef: _queryRef,
	textColor,
}: {
	// TODO: Replace `null` with `siteNavigation_AtlassianHubAppShell$key` once siteTree is available.
	queryRef: null;
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const slotRef = useRef<HTMLDivElement>(null);
	const measurementRowRef = useRef<HTMLDivElement>(null);
	const items = EMPTY_SITE_NAVIGATION_ITEMS;
	const { overflowItems, promotedFolders } = useSiteNavigationOverflow({
		items,
		measurementRowRef,
		slotRef,
	});
	const folders = items.filter(
		(item): item is HelpCenterFolder => item.__typename === 'HelpCenterFolder',
	);

	return (
		<Box ref={slotRef} testId="nav-links-center-slot" xcss={styles.centerSlot}>
			<SiteNavigationMenus
				overflowItems={overflowItems}
				promotedFolders={promotedFolders}
				textColor={textColor}
			/>
			<Box
				aria-hidden="true"
				ref={measurementRowRef}
				testId="site-navigation-measurement-row"
				xcss={styles.measurementRow}
			>
				{folders.map((folder) => (
					<Box data-site-navigation-folder-id={folder.id} key={folder.id}>
						<Button
							appearance="subtle"
							iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
							tabIndex={-1}
						>
							{folder.name?.default}
						</Button>
					</Box>
				))}
				<Box data-site-navigation-more-item>
					<Button
						appearance="subtle"
						iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
						tabIndex={-1}
					>
						{formatMessage(messages.more)}
					</Button>
				</Box>
			</Box>
		</Box>
	);
};
